import { emptyV2Api as api } from "../../emptyV2Api";

export type RazorpayPaymentData = {
  id?: string;
  entity?: string;
  amount?: number;
  amount_paid?: number;
  amount_due?: number;
  currency?: string;
  receipt?: string | null;
  status?: string;
  attempts?: number;
  notes?: Record<string, unknown> | string | null;
  created_at?: number;
  captured?: boolean;
  error_code?: string | null;
  [key: string]: unknown;
};

export type RazorpayPaymentsResponse = {
  entity?: string;
  count?: number;
  items?: RazorpayPaymentData[];
  [key: string]: unknown;
};

export type RazorpayPromoData = {
  promoCode?: string;
  discountAmount?: number;
  discountType?: string;
  discountValue?: number;
  [key: string]: unknown;
};

export type RazorpayOrderDetails = {
  OrderDt?: string;
  currency?: string;
  email?: string;
  notes?: unknown;
  promoData?: RazorpayPromoData | null;
  receipt?: string;
  skus?: Record<string, number>;
  sub?: string;
  rzp_create_order_response?: RazorpayPaymentData;
  rzp_payment_process_response?: RazorpayPaymentData;
  rzp_payments_response?: RazorpayPaymentsResponse;
  [key: string]: unknown;
};

export type RazorpayLoyaltyPointAward = {
  UserId?: string;
  EventId?: string;
  PointsDelta?: number;
  EventType?: string;
  Metadata?: string;
  CreatedAt?: string;
  [key: string]: unknown;
};

export type RazorpayLoyaltyPointAccount = {
  UserId?: string;
  Email?: string;
  [key: string]: unknown;
};

export type RazorpayOrder = {
  OrderId: string;
  ShopId: number;
  MarketplacePlatform?: string;
  OrderDetails?: RazorpayOrderDetails;
  Status?: string;
  SimplyWsUserSub?: string;
  OrderDt?: string;
  LoyaltyPointAwards?: RazorpayLoyaltyPointAward[];
  LoyaltyPointsAwarded?: number;
  LoyaltyPointAccounts?: string[];
  LoyaltyPointAccountEmails?: string[];
  LoyaltyPointAccountDetails?: RazorpayLoyaltyPointAccount[];
  RazorpayPayments?: RazorpayPaymentsResponse;
  [key: string]: unknown;
};

export type RazorpayOrdersResponse = {
  ShopId: number;
  Count: number;
  Orders: RazorpayOrder[];
};

export type RazorpayOrderPaymentsResponse = {
  OrderId?: string;
  ShopId?: number;
  Cached?: boolean;
  RazorpayPayments?: RazorpayPaymentsResponse;
};

type RawRazorpayOrdersResponse =
  | RazorpayOrdersResponse
  | RazorpayOrder[]
  | {
      statusCode?: number | string;
      body?: string | RazorpayOrdersResponse | RazorpayOrder[];
    };

type RawRazorpayOrderPaymentsResponse =
  | RazorpayOrderPaymentsResponse
  | {
      statusCode?: number | string;
      body?: string | RazorpayOrderPaymentsResponse;
    };

export const addTagTypes = ["RazorpayOrders"] as const;

function unwrapResponse<T>(response: T | { body?: string | T }): T | undefined {
  if (response && typeof response === "object" && "body" in response && response.body) {
    return typeof response.body === "string"
      ? JSON.parse(response.body)
      : response.body;
  }

  return response as T;
}

const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getRazorpayOrders: build.query<RazorpayOrdersResponse, void>({
        query: () => ({
          url: "/webshop/razorpay/orders",
          method: "GET",
        }),
        transformResponse: (response: RawRazorpayOrdersResponse) => {
          const payload = unwrapResponse<RazorpayOrdersResponse | RazorpayOrder[]>(response);

          if (Array.isArray(payload)) {
            return {
              ShopId: 1,
              Count: payload.length,
              Orders: payload,
            };
          }

          return {
            ShopId: payload?.ShopId ?? 1,
            Count: payload?.Count ?? payload?.Orders?.length ?? 0,
            Orders: payload?.Orders ?? [],
          };
        },
        providesTags: ["RazorpayOrders"],
      }),
      getRazorpayOrderPayments: build.query<RazorpayOrderPaymentsResponse, string>({
        query: (orderId) => ({
          url: "/webshop/razorpay/orders",
          method: "GET",
          params: {
            order_id: orderId,
          },
        }),
        transformResponse: (response: RawRazorpayOrderPaymentsResponse) => {
          return unwrapResponse<RazorpayOrderPaymentsResponse>(response) ?? {};
        },
      }),
    }),
  });

export const {
  useGetRazorpayOrderPaymentsQuery,
  useGetRazorpayOrdersQuery,
} = injectedRtkApi;
